/**
 * Global Fallback Sources Panel
 * Manage configurable fallback sources for POC scans
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Globe, GripVertical } from "lucide-react";

interface GlobalFallbackSource {
  id: string;
  name: string;
  url: string;
  description: string | null;
  is_enabled: boolean;
  priority: number;
}

interface GlobalFallbackSourcesPanelProps {
  useGlobalFallback: boolean;
  onToggleGlobalFallback: (enabled: boolean) => void;
}

export function GlobalFallbackSourcesPanel({ 
  useGlobalFallback, 
  onToggleGlobalFallback 
}: GlobalFallbackSourcesPanelProps) {
  const [sources, setSources] = useState<GlobalFallbackSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New source form
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchSources = async () => {
    try {
      const { data, error } = await supabase
        .from("global_fallback_sources")
        .select("*")
        .order("priority", { ascending: true });

      if (error) throw error;
      setSources(data || []);
    } catch (err) {
      console.error("Error fetching global fallback sources:", err);
      toast.error("Failed to load fallback sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const handleAddSource = async () => {
    if (!newName.trim() || !newUrl.trim()) {
      toast.error("Name and URL are required");
      return;
    }

    setSaving(true);
    try {
      // Get max priority to add at end
      const maxPriority = sources.reduce((max, s) => Math.max(max, s.priority), 0);
      
      const { error } = await supabase
        .from("global_fallback_sources")
        .insert({
          name: newName.trim(),
          url: newUrl.trim(),
          description: newDescription.trim() || null,
          is_enabled: true,
          priority: maxPriority + 10,
        });

      if (error) throw error;

      toast.success("Fallback source added");
      setNewName("");
      setNewUrl("");
      setNewDescription("");
      setShowAddForm(false);
      fetchSources();
    } catch (err: any) {
      console.error("Error adding source:", err);
      if (err.code === "23505") {
        toast.error("This URL already exists");
      } else {
        toast.error("Failed to add source");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (source: GlobalFallbackSource) => {
    try {
      const { error } = await supabase
        .from("global_fallback_sources")
        .update({ is_enabled: !source.is_enabled })
        .eq("id", source.id);

      if (error) throw error;

      setSources(prev => prev.map(s => 
        s.id === source.id ? { ...s, is_enabled: !s.is_enabled } : s
      ));
      toast.success(source.is_enabled ? "Source disabled" : "Source enabled");
    } catch (err) {
      console.error("Error toggling source:", err);
      toast.error("Failed to update source");
    }
  };

  const handleDeleteSource = async (source: GlobalFallbackSource) => {
    if (!confirm(`Delete "${source.name}"?`)) return;

    try {
      const { error } = await supabase
        .from("global_fallback_sources")
        .delete()
        .eq("id", source.id);

      if (error) throw error;

      setSources(prev => prev.filter(s => s.id !== source.id));
      toast.success("Source deleted");
    } catch (err) {
      console.error("Error deleting source:", err);
      toast.error("Failed to delete source");
    }
  };

  const enabledCount = sources.filter(s => s.is_enabled).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Global Fallback Sources
          {enabledCount > 0 && (
            <Badge variant="secondary">{enabledCount} enabled</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Configure fallback sources to try when all merchant-specific sources fail.
          These are used as a last resort for content discovery.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Master toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
          <div>
            <Label htmlFor="use-global-fallback" className="text-base font-medium">
              Enable Global Fallback Sources
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              When enabled, these sources will be tried after all merchant-specific sources fail
            </p>
          </div>
          <Switch
            id="use-global-fallback"
            checked={useGlobalFallback}
            onCheckedChange={onToggleGlobalFallback}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            {/* Source list */}
            <div className="space-y-2">
              {sources.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No fallback sources configured yet
                </p>
              ) : (
                sources.map((source) => (
                  <div
                    key={source.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg ${
                      !source.is_enabled ? "opacity-50 bg-muted/20" : ""
                    }`}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{source.name}</span>
                        <Badge variant="outline" className="text-xs shrink-0">
                          priority: {source.priority}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {source.url}
                      </p>
                      {source.description && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {source.description}
                        </p>
                      )}
                    </div>

                    <Switch
                      checked={source.is_enabled}
                      onCheckedChange={() => handleToggleEnabled(source)}
                      className="shrink-0"
                    />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteSource(source)}
                      className="shrink-0 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            {/* Add new source form */}
            {showAddForm ? (
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <div>
                  <Label htmlFor="new-source-name">Name</Label>
                  <Input
                    id="new-source-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g., Flyerify"
                  />
                </div>
                <div>
                  <Label htmlFor="new-source-url">URL</Label>
                  <Input
                    id="new-source-url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="https://example.com/"
                  />
                </div>
                <div>
                  <Label htmlFor="new-source-description">Description (optional)</Label>
                  <Input
                    id="new-source-description"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Brief description of this source"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddSource} disabled={saving} size="sm">
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Add Source
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setShowAddForm(false);
                      setNewName("");
                      setNewUrl("");
                      setNewDescription("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(true)}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Fallback Source
              </Button>
            )}
          </>
        )}

        {/* Info box */}
        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>How it works:</strong> If all merchant-specific sources fail to provide usable content, 
            the system will attempt these global sources (like Flyerify) to find flyer content. 
            Sources are tried in priority order (lower = first).
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
